import React, { useState, useEffect } from "react";
import { Button, Progress, message, Spin } from "antd";
import { withRouter } from "react-router-dom";

// Import all 8 sections
import Section1 from "../BusinessSignupForm/BuisinessSections/Section1";
import Section2 from "../BusinessSignupForm/BuisinessSections/Section2";
import Section3 from "../BusinessSignupForm/BuisinessSections/Section3";
import Section4 from "../BusinessSignupForm/BuisinessSections/Section4";
import Section5 from "../BusinessSignupForm/BuisinessSections/Section5";
import Section6 from "../BusinessSignupForm/BuisinessSections/Section6";
import Section7 from "../BusinessSignupForm/BuisinessSections/Section7";
import Section8 from "../BusinessSignupForm/BuisinessSections/Section8";

import MatchedCompanies from "./MatchedCompanies";

import ThankYouScreen from "./ThankYouScreen";
import { addQuestions } from "../../Api/RegistrationApis";
import { update } from "../../Api/Apis";
import AppConfig from "../config/AppConfig";
import "./BusinessSignupForm.css";

const sectionTitles = [
  "Section 1 - Business Profile",
  "Section 2 - Industry, Entity & Service Area",
  "Section 3 - Campaign Objectives & Success Metrics",
  "Section 4 - Advertising Experience",
  "Section 5 - Audience Targeting",
  "Section 6 - Tools, Assets & Positioning",
  "Section 7 - Competition & Timing",
  "Section 8 - Budget & References",
];

const sections = [
  Section1,
  Section2,
  Section3,
  Section4,
  Section5,
  Section6,
  Section7,
  Section8,
];

const BusinessSignupForm = (props) => {
  const [step, setStep] = useState(0);
  const [formValues, setFormValues] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showCompanies, setShowCompanies] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("BusinessPrefill");
      const goto = localStorage.getItem("BusinessGotoStep");
      if (raw) {
        const prefill = JSON.parse(raw);
        if (prefill && typeof prefill === "object") {
          setFormValues((prev) => ({ ...prev, ...prefill }));
        }
      }
      if (goto) {
        const n = parseInt(goto, 10);
        if (!isNaN(n)) setStep(n); // 1 => Section 2
      }
    } catch (e) {
      console.warn("Prefill parse error", e);
    } finally {
      localStorage.removeItem("BusinessPrefill");
      localStorage.removeItem("BusinessGotoStep");
    }
  }, []);

  const CurrentSection = sections[step];
  const progressPercent = ((step + 1) / sections.length) * 100;

  const getQuestionnaireId = () => {
    const raw = localStorage.getItem("BusinessQuestionnaireId");
    try {
      return raw ? JSON.parse(raw) : null;
    } catch {
      return raw;
    }
  };

  const handleNext = async () => {
    if (step === 0 && formValues?.signedInExisting) {
      const id =
        formValues?.questionnaireId ||
        formValues?.id ||
        JSON.parse(localStorage.getItem("BusinessQuestionnaireId") || "null");
      if (id && !localStorage.getItem("BusinessQuestionnaireId")) {
        localStorage.setItem("BusinessQuestionnaireId", JSON.stringify(id));
      }
      setStep((s) => s + 1);
      return;
    }
    if (step === 0) {
      const requiredFields = [
        { key: "businessName", label: "Business Name" },
        { key: "contactName", label: "Contact Name" },
        { key: "contactTitle", label: "Contact Title" },
        { key: "email", label: "Email" },
        { key: "phone", label: "Phone Number" },
        { key: "street", label: "Street Address" },
        { key: "city", label: "City" },
        { key: "state", label: "State" },
        { key: "zip", label: "Zip Code" },
      ];

      for (let field of requiredFields) {
        if (!formValues[field.key] || formValues[field.key].trim() === "") {
          message.error(`Please fill in the ${field.label}.`);
          return;
        }
      }
      const section1Payload = {
        businessName: formValues.businessName || "",
        contactName: formValues.contactName || "",
        contactTitle: formValues.contactTitle || "",
        email: formValues.email || "",
        phone: formValues.phone || "",
        street: formValues.street || "",
        city: formValues.city || "",
        state: formValues.state || "",
        zip: formValues.zip || "",
      };

      setIsLoading(true);

      try {
        const response = await addQuestions(
          AppConfig["entityNames"]["questionnaires"],
          { entityData: section1Payload }
        );

        const msg = response?.data?.message || response?.message;
        const code = response?.data?.code || response?.code;
        const statusCode = response?.data?.statusCode;

        if (code === "UsernameExistsException" || statusCode === 0) {
          message.error(
            msg || "This business already exists, please verify/sign in."
          );
          setIsLoading(false);
          return;
        }

        if (response?.success) {
          const questionnaireId =
            response.data?.data?.id || response.id || response.data?.id;
          if (!questionnaireId) {
            message.error("Could not read questionnaire id from server.");
            return;
          }
          localStorage.setItem(
            "BusinessQuestionnaireId",
            JSON.stringify(questionnaireId)
          );
          // also keep it in state if you want
          setFormValues((v) => ({ ...v, questionnaireId }));
          try {
            await update(
              AppConfig["entityNames"]["questionnaires"],
              questionnaireId,
              { entityData: { promoUsed: false, promoCode: null } }
            );
          } catch (e) {
            console.warn("Could not initialize promo flags", e);
          }
          message.success("Business registered successfully.");
          setStep(step + 1);
        } else {
          message.error("Failed to register business.");
        }
      } catch (err) {
        console.error("Registration API error:", err);
        message.error("Something went wrong during registration.");
      } finally {
        setIsLoading(false);
      }

      return;
    }

    if (step === 1) {
      const id = getQuestionnaireId();
      if (!id) {
        message.error("Session expired. Please restart the form.");
        return;
      }

      if (!formValues.industries || formValues.industries.length === 0) {
        message.error("Please select at least one industry.");
        return;
      }

      if (!formValues.entityType || formValues.entityType.trim() === "") {
        message.error("Please select your business entity type.");
        return;
      }

      if (!formValues.counties || formValues.counties.length === 0) {
        message.error(
          "Please select at least one county where you offer services."
        );
        return;
      }

      const payload = {
        entityData: {
          industry: Array.isArray(formValues.industries)
            ? formValues.industries
            : formValues.industries
            ? [formValues.industries]
            : [],
          entityType: formValues.entityType || "",
          service: formValues.counties || [],
        },
      };

      setIsLoading(true);
      try {
        const response = await update(
          AppConfig["entityNames"]["questionnaires"],
          id,
          payload
        );

        if (response?.success) {
          setStep(step + 1);
        } else {
          message.error("Failed to save Section 2. Please check your inputs.");
        }
      } catch (err) {
        console.error("Section 2 update error:", err);
        message.error("Something went wrong while saving Section 2.");
      } finally {
        setIsLoading(false);
      }

      return;
    }

    if (step === 2) {
      const id = getQuestionnaireId();
      if (!id) {
        message.error("Session expired. Please restart the form.");
        return;
      }

      // if (
      //   (!formValues.objectivesAwareness ||
      //     formValues.objectivesAwareness.length === 0) &&
      //   (!formValues.objectivesEngagement ||
      //     formValues.objectivesEngagement.length === 0) &&
      //   (!formValues.objectivesAction ||
      //     formValues.objectivesAction.length === 0)
      // ) {
      //   message.error("Please select at least one marketing objective.");
      //   return;
      // }

      // if (
      //   !formValues.successMetrics ||
      //   formValues.successMetrics.length === 0
      // ) {
      //   message.error("Please select at least one metric of success.");
      //   return;
      // }

      // if (!formValues.startTime || formValues.startTime.trim() === "") {
      //   message.error(
      //     "Please select when you plan to begin working with clients."
      //   );
      //   return;
      // }

      const payload = {
        entityData: {
          objectivesAwareness: formValues.objectivesAwareness || [],
          objectivesEngagement: formValues.objectivesEngagement || [],
          objectivesAction: formValues.objectivesAction || [],
          successMetrics: formValues.successMetrics || [],
          startTime: formValues.startTime || "",
        },
      };

      setIsLoading(true);
      try {
        const response = await update(
          AppConfig.entityNames.questionnaires,
          id,
          payload
        );

        if (response?.success) {
          setStep(step + 1); // Proceed to Section 4
        } else {
          message.error("Failed to save Section 3. Please check your answers.");
        }
      } catch (err) {
        console.error("Section 3 update failed:", err);
        message.error("Something went wrong while saving Section 3.");
      } finally {
        setIsLoading(false);
      }

      return;
    }

    if (step === 3) {
      const id = getQuestionnaireId();
      if (!id) {
        message.error("Session expired. Please restart the form.");
        return;
      }

      // Q13 must be answered
      if (!formValues.hasSpent) {
        message.error(
          "Please answer whether you've spent money on advertising."
        );
        return;
      }

      // If user selected "No", skip validation of Q14–Q17 and move on
      if (formValues.hasSpent === "No") {
        const payload = {
          entityData: {
            hasSpent: "No",
            // Q18 is optional when No; include only if user picked it
            ...(formValues.adFrequency
              ? { adFrequency: formValues.adFrequency }
              : {}),
          },
        };

        setIsLoading(true);
        try {
          const response = await update(
            AppConfig.entityNames.questionnaires,
            id,
            payload
          );
          if (response?.success) {
            setStep(step + 1); // proceed to Section 5
          } else {
            message.error(
              "Failed to save Section 4. Please check your inputs."
            );
          }
        } catch (err) {
          console.error("Section 4 update failed:", err);
          message.error("Something went wrong while saving Section 4.");
        } finally {
          setIsLoading(false);
        }
        return;
      }

      // From here on, validations apply ONLY when Q13 = "Yes"
      // Q14
      if (!formValues.adTypes || formValues.adTypes.length === 0) {
        message.error("Please select at least one type of advertising.");
        return;
      }
      if (
        formValues.adTypes.includes("Other") &&
        !formValues.adTypesOther?.trim()
      ) {
        message.error("Please specify the 'Other' advertising type.");
        return;
      }

      // Q15
      if (!formValues.adReasons || formValues.adReasons.length === 0) {
        message.error("Please select at least one reason for advertising.");
        return;
      }
      if (
        formValues.adReasons.includes("Other") &&
        !formValues.adReasonsOther?.trim()
      ) {
        message.error("Please specify the 'Other' reason for advertising.");
        return;
      }

      // Q16
      if (!formValues.adEffectiveness) {
        message.error("Please indicate how effective the advertising was.");
        return;
      }

      if (
        ["Yes", "Somewhat"].includes(formValues.adEffectiveness) &&
        (!formValues.adResults || formValues.adResults.length === 0)
      ) {
        message.error("Please select the results you experienced.");
        return;
      }
      if (
        ["Yes", "Somewhat"].includes(formValues.adEffectiveness) &&
        formValues.adResults?.includes("Other") &&
        !formValues.adResultsOther?.trim()
      ) {
        message.error("Please specify the 'Other' result.");
        return;
      }

      if (
        formValues.adEffectiveness === "No" &&
        (!formValues.adFailures || formValues.adFailures.length === 0)
      ) {
        message.error("Please select the failures you experienced.");
        return;
      }
      if (
        formValues.adEffectiveness === "No" &&
        formValues.adFailures?.includes("Other") &&
        !formValues.adFailuresOther?.trim()
      ) {
        message.error("Please specify the 'Other' failure.");
        return;
      }

      // Q17
      if (!formValues.adWorth) {
        message.error("Please share whether the advertising was worth it.");
        return;
      }

      // Q18 (when Yes: include if provided; you can require it if you want)
      // if (!formValues.adFrequency) {
      //   message.error("Please indicate how often you advertise.");
      //   return;
      // }

      // Build payload for "Yes"
      const payload = {
        entityData: {
          hasSpent: "Yes",

          // Q14
          adTypes: formValues.adTypes || [],
          ...(formValues.adTypes?.includes("Other") && {
            adTypesOther: formValues.adTypesOther || "",
          }),

          // Q15
          adReasons: formValues.adReasons || [],
          ...(formValues.adReasons?.includes("Other") && {
            adReasonsOther: formValues.adReasonsOther || "",
          }),

          // Q16
          adEffectiveness: formValues.adEffectiveness,

          ...(["Yes", "Somewhat"].includes(formValues.adEffectiveness)
            ? {
                adResults: formValues.adResults || [],
                ...(formValues.adResults?.includes("Other") && {
                  adResultsOther: formValues.adResultsOther || "",
                }),
              }
            : {}),

          ...(formValues.adEffectiveness === "No"
            ? {
                adFailures: formValues.adFailures || [],
                ...(formValues.adFailures?.includes("Other") && {
                  adFailuresOther: formValues.adFailuresOther || "",
                }),
              }
            : {}),

          // Q17
          adWorth: formValues.adWorth,

          // Q18 (optional)
          ...(formValues.adFrequency
            ? { adFrequency: formValues.adFrequency }
            : {}),
        },
      };

      setIsLoading(true);
      try {
        const response = await update(
          AppConfig.entityNames.questionnaires,
          id,
          payload
        );
        if (response?.success) {
          setStep(step + 1);
        } else {
          message.error(" Failed to save Section 4. Please check your inputs.");
        }
      } catch (err) {
        console.error("Section 4 update failed:", err);
        message.error("Something went wrong while saving Section 4.");
      } finally {
        setIsLoading(false);
      }

      return;
    }

    if (step === 4) {
      const id = getQuestionnaireId();
      if (!id) {
        message.error("Session expired. Please restart the form.");
        return;
      }

      if (!formValues.gender || formValues.gender.length === 0) {
        message.error("Please select at least one gender group.");
        return;
      }

      if (!formValues.household || formValues.household.length === 0) {
        message.error("Please select at least one household type.");
        return;
      }

      if (!formValues.lifeStage || formValues.lifeStage.length === 0) {
        message.error("Please select at least one life stage.");
        return;
      }

      if (!formValues.ethnicGroups || formValues.ethnicGroups.length === 0) {
        message.error("Please select at least one racial or ethnic group.");
        return;
      }

      if (
        formValues.ethnicGroups.includes("Other") &&
        !formValues.ethnicGroupsOther?.trim()
      ) {
        message.error("Please specify the 'Other' ethnic group.");
        return;
      }
      const fixDash = (s) => (s || "").replace(/-/g, "-");
      const payload = {
        entityData: {
          // Q19 – Audience Types
          gender: formValues.gender || [],
          household: formValues.household || [],
          lifeStage: formValues.lifeStage || [],

          // Q20 – Age Groups (optional)
          ...(formValues.ageGroups?.length > 0 && {
            agegroup: formValues.ageGroups.map(fixDash),
          }),

          // Q21 – Racial / Ethnic Groups
          ethnicGroups: formValues.ethnicGroups || [],
          ...(formValues.ethnicGroups?.includes("Other") && {
            ethnicGroupsOther: formValues.ethnicGroupsOther || "",
          }),
        },
      };

      setIsLoading(true);
      try {
        const response = await update(
          AppConfig.entityNames.questionnaires,
          id,
          payload
        );

        if (response?.success) {
          setStep(step + 1); // Proceed to Section 6
        } else {
          message.error(" Failed to save Section 5. Please try again.");
        }
      } catch (err) {
        console.error("Error in Section 5 update:", err);
        message.error(" Error saving Section 5.");
      } finally {
        setIsLoading(false);
      }

      return;
    }

    if (step === 5) {
      const id = getQuestionnaireId();
      if (!id) {
        message.error("Session expired. Please restart the form.");
        return;
      }

      if (
        !formValues.marketingAssets ||
        formValues.marketingAssets.length === 0
      ) {
        message.error("Please select at least one marketing asset/tool.");
        return;
      }

      if (
        formValues.marketingAssets.includes("Other") &&
        !formValues.marketingAssetsOther?.trim()
      ) {
        message.error("Please specify the 'Other' marketing asset.");
        return;
      }

      if (!formValues.assetsStatus) {
        message.error("Please select the status of your tools/assets.");
        return;
      }

      if (
        formValues.assetsStatus.toLowerCase().includes("update") &&
        !formValues.lastUpdatedAssets?.trim()
      ) {
        message.error(
          "Please specify when your assets/tools were last updated."
        );
        return;
      }

      if (
        !formValues.businessHighlights ||
        formValues.businessHighlights.length === 0
      ) {
        message.error("Please select at least one business highlight.");
        return;
      }

      if (
        formValues.businessHighlights.includes("Other") &&
        !formValues.businessHighlightsOther?.trim()
      ) {
        message.error("Please specify the 'Other' business highlight.");
        return;
      }

      const payload = {
        entityData: {
          // Q22 – Marketing Tools / Assets
          marketingAssets: formValues.marketingAssets || [],
          ...(formValues.marketingAssets?.includes("Other") && {
            marketingAssetsOther: formValues.marketingAssetsOther || "",
          }),

          // Q23 – Are tools/assets up to date?
          assetsStatus: formValues.assetsStatus || "",
          ...(formValues.assetsStatus?.includes("update") && {
            lastUpdatedAssets: formValues.lastUpdatedAssets || "",
          }),

          // Q24 – Business Highlights
          businessHighlights: formValues.businessHighlights || [],
          ...(formValues.businessHighlights?.includes("Other") && {
            businessHighlightsOther: formValues.businessHighlightsOther || "",
          }),
        },
      };

      setIsLoading(true);
      try {
        const response = await update(
          AppConfig.entityNames.questionnaires,
          id,
          payload
        );

        if (response?.success) {
          setStep(step + 1); // Proceed to Section 7
        } else {
          message.error(" Failed to save Section 6. Please try again.");
        }
      } catch (err) {
        console.error("Error in Section 6 update:", err);
        message.error(" Error saving Section 6.");
      } finally {
        setIsLoading(false);
      }

      return;
    }

    if (step === 6) {
      const id = getQuestionnaireId();
      if (!id) {
        message.error("Session expired. Please restart the form.");
        return;
      }

      if (!formValues.competition || formValues.competition.length === 0) {
        message.error("Please select at least one competition option.");
        return;
      }

      if (
        formValues.competition.includes("Other") &&
        !formValues.otherCompetition?.trim()
      ) {
        message.error("Please specify the 'Other' competition.");
        return;
      }

      if (
        !formValues.competitionImpact ||
        formValues.competitionImpact.length === 0
      ) {
        message.error("Please select how competition impacts your business.");
        return;
      }

      if (
        formValues.competitionImpact.includes("Other") &&
        !formValues.otherCompetitionImpact?.trim()
      ) {
        message.error("Please specify the 'Other' competition impact.");
        return;
      }

      if (!formValues.startPromotion) {
        message.error("Please select when you plan to start promotion.");
        return;
      }

      if (!formValues.budget) {
        message.error("Please select your estimated budget.");
        return;
      }
      const normRange = (r) => (r || "").replace(/k/g, "K");

      const payload = {
        entityData: {
          // Q25 – Competition
          competition: formValues.competition || [],
          ...(formValues.competition?.includes("Other") && {
            otherCompetition: formValues.otherCompetition || "",
          }),

          // Q26 – Competition Impact
          competitionImpact: formValues.competitionImpact || [],
          ...(formValues.competitionImpact?.includes("Other") && {
            otherCompetitionImpact: formValues.otherCompetitionImpact || "",
          }),

          // Q27 – Promotion Start Timeline
          startPromotion: formValues.startPromotion || "",

          // Q28 – Estimated Budget
          rangeGoals: normRange(formValues.budget || ""),
        },
      };
      if (formValues.budget) {
        localStorage.setItem(
          "lastBusinessRangeGoals",
          JSON.stringify(formValues.budget)
        );
      }

      setIsLoading(true);
      try {
        const response = await update(
          AppConfig.entityNames.questionnaires,
          id,
          payload
        );

        if (response?.success) {
          setStep(step + 1); // Go to Section 8
        } else {
          message.error("Failed to save Section 7. Please try again.");
        }
      } catch (err) {
        console.error("Error in Section 7 update:", err);
        message.error("Error saving Section 7.");
      } finally {
        setIsLoading(false);
      }

      return;
    }

    if (step === 7) {
      const id = getQuestionnaireId();
      if (!id) {
        message.error("Session expired. Please restart the form.");
        return;
      }

      if (!formValues.contactMethod) {
        message.error("Please select your preferred contact method.");
        return;
      }

      if (formValues.permission === undefined) {
        message.error("Please confirm if we can send you information.");
        return;
      }

      const payload = {
        entityData: {
          preferredContact:
            formValues.contactMethod === "Email" ? "E-Mail" : "Phone",
          information: formValues.permission || false,
        },
      };

      setIsLoading(true);
      try {
        const response = await update(
          AppConfig.entityNames.questionnaires,
          id,
          payload
        );

        if (response?.success) {
          localStorage.setItem("Step8", JSON.stringify(id));
          setIsSubmitted(true); // Show Thank You screen
        } else {
          message.error("Failed to save Section 8. Please check your answers.");
        }
      } catch (err) {
        console.error("Section 8 update failed:", err);
        message.error("Something went wrong while saving Section 8.");
      } finally {
        setIsLoading(false);
      }

      return;
    }

    setStep(step + 1);
  };

  const handleBack = () => setStep(step - 1);

  return (
    <div className="business-signup-container">
      {isSubmitted ? (
        showCompanies ? (
          <MatchedCompanies
            formData={formValues}
            gotoPayment={() => {
              const questionnaireId = JSON.parse(
                localStorage.getItem("Step8") || "null"
              );

              if (!questionnaireId) {
                message.error("Questionnaire ID missing. Please try again.");
                return;
              }

              props.history.push({
                pathname: "/app/PaymentScreen",
                state: {
                  questionnaireId,
                  rangeGoals: formValues?.rangeGoals || formValues?.budget,
                },
              });
            }}
          />

          // <MatchedCompanies
          //   formData={formValues}
          //   gotoPayment={() => {
          //     const id = localStorage.getItem("Step8");
          //     props.history.push({
          //       pathname: `/app/PaymentScreen/${id}`,
          //       state: {
          //         rangeGoals: formValues?.rangeGoals || formValues?.budget,
          //       },
          //     });
          //   }}
          // />
        ) : (
          <ThankYouScreen onContinue={() => setShowCompanies(true)} />
        )
      ) : (
        <Spin spinning={isLoading} tip="Processing...">
          <h1 style={{ textAlign: "center" }}>Join as a Business</h1>

          <h2 style={{ marginBottom: "15px", color: "#444" }}>
            {sectionTitles[step]}
          </h2>

          <Progress percent={progressPercent} style={{ marginBottom: 20 }} />

          <CurrentSection
            formValues={formValues}
            setFormValues={setFormValues}
            setIsLoading={setIsLoading}
          />

          <div style={{ marginTop: 20 }}>
            {step > 0 && (
              <Button style={{ marginRight: 8 }} onClick={handleBack}>
                Back
              </Button>
            )}

            {step < sections.length - 1 && (
              <Button type="primary" onClick={handleNext}>
                Next
              </Button>
            )}

            {step === sections.length - 1 && (
              <Button type="primary" onClick={handleNext}>
                Submit
              </Button>
            )}
          </div>
        </Spin>
      )}
    </div>
  );
};

export default withRouter(BusinessSignupForm);
